import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch, isAdmin } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip } from './ui/kit';

/**
 * Clinical product information, for somebody standing in a clinic.
 *
 * Two things drove the design.
 *
 * Search comes first, because a representative is asked a question and has
 * seconds to answer. Nobody reads a dossier front to back in front of a
 * nurse.
 *
 * Contraindications are shown before benefits, in red, on the summary card
 * as well as the detail. In the source document the line that matters most
 * about the honey gauze -- that it must not go on a dry wound without Hera
 * Gel underneath -- sits mid-paragraph between two benefit claims. Anything
 * that can be skimmed past will be.
 *
 * Nothing here is editable. What a representative says about a medical
 * device is a product claim.
 */

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 14,
};

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail || j.message || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const TABS = [
  ['composition', 'What is in it'],
  ['indications', 'When to use'],
  ['how_to_use', 'How to use'],
  ['mechanism', 'How it works'],
  ['storage', 'Storage'],
  ['evidence', 'Evidence'],
];

export default function ClinicalDesk({ notify }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [tab, setTab] = useState('composition');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState(null);
  const [resources, setResources] = useState([]);
  const [seminar, setSeminar] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const admin = isAdmin();

  const load = useCallback(async () => {
    setError(null);
    try {
      const [d, r] = await Promise.all([
        req('/api/clinical/dossiers'),
        req('/api/clinical/resources').catch(() => []),
      ]);
      setList(d);
      setResources(Array.isArray(r) ? r : []);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doSearch = async (term) => {
    const text_ = (term ?? q).trim();
    if (text_.length < 2) { setHits(null); return; }
    setBusy(true);
    try {
      setHits(await req(`/api/clinical/search?q=${encodeURIComponent(text_)}`));
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const openDossier = async (code) => {
    setBusy(true);
    try {
      setOpen(await req(`/api/clinical/dossiers/${code}`));
      setTab('composition');
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (error) return <Banner tone="danger" title="Could not load">{error}</Banner>;

  const nothingLoaded = list && list.length === 0;

  return (
    <div>
      {/* ---- search first ------------------------------------------------ */}
      <Card style={{ marginBottom: space(2) }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>
          Ask a question
        </h3>
        <div style={{ fontSize: 12.5, color: color.textSecondary,
                      marginBottom: space(1) }}>
          Type what you were asked. If nothing matches, say you will check and
          come back &mdash; do not improvise about a medical device.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={inputStyle} value={q} autoFocus
                 placeholder="iodine allergy, dry wound, how often to change&hellip;"
                 onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }} />
          <Btn onClick={() => doSearch()} disabled={busy}>
            {busy ? '…' : 'Search'}
          </Btn>
        </div>

        {hits && (
          <div style={{ marginTop: space(1.5) }}>
            {hits.count === 0 ? (
              <Banner tone="warning" title="Nothing matched">{hits.note}</Banner>
            ) : (
              hits.results.map((r, i) => (
                <div key={i} style={{ borderTop: `1px solid ${color.border}`,
                                      padding: `${space(1)} 0` }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                    {r.question}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 3 }}>{r.answer}</div>
                  <button onClick={() => openDossier(r.code)}
                          style={{ background: 'none', border: 'none',
                                   padding: 0, marginTop: 4, fontSize: 11.5,
                                   color: color.info, cursor: 'pointer' }}>
                    {r.product} &rarr; open full information
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </Card>

      {nothingLoaded && admin && (
        <Card style={{ marginBottom: space(2) }}>
          <Banner tone="info" title="No clinical information is loaded yet">
            Load the product dossiers and the wound care seminar from the
            company documents. They arrive unapproved: somebody confirms they
            match what is registered before anybody presents them.
          </Banner>
          <Btn size="sm" disabled={busy}
               onClick={async () => {
                 setBusy(true);
                 try {
                   const r = await req('/api/clinical/seed', { method: 'POST' });
                   notify?.(`Loaded ${r.loaded.length} item(s).`, 'success');
                   await load();
                 } catch (e) { notify?.(e.message, 'error'); }
                 finally { setBusy(false); }
               }}>
            Load product information
          </Btn>
        </Card>
      )}

      {/* ---- the products ------------------------------------------------ */}
      {(list || []).map((d) => (
        <Card key={d.code} style={{ marginBottom: space(1.5) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                {d.product_name}
              </div>
              {d.strapline && (
                <div style={{ fontSize: 12.5, color: color.textSecondary }}>
                  {d.strapline}
                </div>
              )}
              <div style={{ fontSize: 13, marginTop: 6 }}>{d.overview}</div>
              {(d.products || []).length > 0 && (
                <div style={{ fontSize: 11.5, color: color.textMuted,
                              marginTop: 6 }}>
                  Covers: {d.products.join(', ')}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              {d.status === 'APPROVED'
                ? <Chip tone="success">Approved</Chip>
                : <Chip tone="warning">Not approved</Chip>}
              <div style={{ fontSize: 11.5, color: color.textMuted,
                            marginTop: 4 }}>
                {d.faq_count} answers
              </div>
            </div>
          </div>

          {/* Before the benefits, never after. */}
          {(d.contraindications || []).length > 0 && (
            <div style={{ background: color.dangerBg, borderRadius: radius.sm,
                          padding: '10px 12px', marginTop: space(1.5),
                          borderLeft: `4px solid ${color.danger}` }}>
              <div style={{ fontSize: 11.5, fontWeight: 700,
                            color: color.danger, textTransform: 'uppercase',
                            letterSpacing: 0.4 }}>
                Do not use
              </div>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13 }}>
                {d.contraindications.map((c, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: space(1.5),
                        flexWrap: 'wrap' }}>
            <Btn size="sm" variant="secondary" onClick={() => openDossier(d.code)}>
              Full information
            </Btn>
            {admin && d.status !== 'APPROVED' && (
              <Btn size="sm" disabled={busy}
                   onClick={async () => {
                     const who = window.prompt(
                       'Who is confirming this matches what is registered '
                       + 'for the product?');
                     if (!who || who.trim().length < 3) return;
                     setBusy(true);
                     try {
                       await req(`/api/clinical/${d.code}/approve`, {
                         method: 'POST',
                         headers: { 'Content-Type': 'application/json' },
                         body: JSON.stringify({ approved_by: who.trim() }),
                       });
                       notify?.('Approved.', 'success');
                       await load();
                     } catch (e) { notify?.(e.message, 'error'); }
                     finally { setBusy(false); }
                   }}>
                Approve
              </Btn>
            )}
          </div>
        </Card>
      ))}

      {/* ---- teaching material ------------------------------------------- */}
      {resources.map((r) => (
        <Card key={r.code} style={{ marginBottom: space(1.5) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{r.title}</div>
              <div style={{ fontSize: 12.5, color: color.textSecondary,
                            marginTop: 3, maxWidth: 620 }}>
                {r.summary}
              </div>
            </div>
            <Btn size="sm" variant="secondary"
                 onClick={() => setSeminar(seminar?.code === r.code ? null : r)}>
              {seminar?.code === r.code ? 'Close' : 'Present'}
            </Btn>
          </div>

          {seminar?.code === r.code && (
            <div style={{ marginTop: space(2) }}>
              {(r.sections || []).map((s, i) => (
                <div key={i} style={{ borderTop: `1px solid ${color.border}`,
                                      padding: `${space(1.5)} 0` }}>
                  <div style={{ fontWeight: 600, fontSize: 14,
                                color: color.royal || color.info }}>
                    {s.heading}
                  </div>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18,
                               fontSize: 13, lineHeight: 1.6 }}>
                    {(s.points || []).map((p, j) => <li key={j}>{p}</li>)}
                  </ul>
                </div>
              ))}
              {(r.references || []).length > 0 && (
                <div style={{ borderTop: `1px solid ${color.border}`,
                              paddingTop: space(1.5), marginTop: space(1) }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700,
                                color: color.textSecondary,
                                textTransform: 'uppercase' }}>
                    References
                  </div>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18,
                               fontSize: 11.5, color: color.textSecondary }}>
                    {r.references.map((ref, j) => <li key={j}>{ref}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card>
      ))}

      {/* ---- one product, in full ---------------------------------------- */}
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(null)}>
          <div className="modal" style={{ maxWidth: 820, width: '95%' }}
               onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0 }}>{open.product_name}</h3>
                <div style={{ fontSize: 12, color: color.textSecondary }}>
                  {open.strapline}
                </div>
              </div>
              <Btn size="sm" variant="secondary" onClick={() => setOpen(null)}>
                Close
              </Btn>
            </div>

            <div className="modal-body"
                 style={{ maxHeight: '74vh', overflowY: 'auto' }}>
              {open.not_approved_warning && (
                <Banner tone="warning" title="Not approved">
                  {open.not_approved_warning}
                </Banner>
              )}

              {/* Always first. */}
              <div style={{ background: color.dangerBg, borderRadius: radius.sm,
                            padding: '12px 14px', margin: `${space(1.5)} 0`,
                            borderLeft: `4px solid ${color.danger}` }}>
                <div style={{ fontSize: 12, fontWeight: 700,
                              color: color.danger, textTransform: 'uppercase',
                              letterSpacing: 0.4 }}>
                  Do not use
                </div>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13.5 }}>
                  {(open.contraindications || []).map((c, i) => (
                    <li key={i} style={{ marginBottom: 3 }}>{c}</li>
                  ))}
                </ul>
                {(open.precautions || []).length > 0 && (
                  <>
                    <div style={{ fontSize: 11.5, fontWeight: 700,
                                  marginTop: 10, color: color.textSecondary,
                                  textTransform: 'uppercase' }}>
                      Take care
                    </div>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18,
                                 fontSize: 12.5 }}>
                      {open.precautions.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap',
                            marginBottom: space(1.5) }}>
                {TABS.map(([k, label]) => (
                  <Btn key={k} size="sm"
                       variant={tab === k ? 'primary' : 'secondary'}
                       onClick={() => setTab(k)}>{label}</Btn>
                ))}
              </div>

              {tab === 'composition' ? (
                (open.composition || []).map((c, i) => (
                  <div key={i} style={{ borderTop: `1px solid ${color.border}`,
                                        padding: '8px 0' }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                      {c.name}
                      {c.strength && (
                        <span style={{ color: color.info, marginLeft: 6 }}>
                          {c.strength}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: color.textSecondary }}>
                      {c.role}
                    </div>
                  </div>
                ))
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5,
                             lineHeight: 1.65 }}>
                  {(open[tab] || []).map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              )}

              <div style={{ borderTop: `1px solid ${color.border}`,
                            marginTop: space(2), paddingTop: space(1.5) }}>
                <h4 style={{ margin: `0 0 ${space(1)}`, fontSize: 14 }}>
                  Questions you will be asked
                </h4>
                {(open.faqs || []).map((f) => (
                  <details key={f.id} style={{ marginBottom: 6 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 13.5,
                                      fontWeight: 600 }}>
                      {f.question}
                    </summary>
                    <div style={{ fontSize: 13, padding: '6px 0 0 14px' }}>
                      {f.answer}
                    </div>
                  </details>
                ))}
              </div>

              <div style={{ fontSize: 11, color: color.textMuted,
                            marginTop: space(2) }}>
                Source: {open.source_document} &middot; version {open.version}
                {open.approved_by && ` · approved by ${open.approved_by}`}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
